import type { LiveSheetAbility, LiveSheetSpell, LiveLockedCircle } from "../../types/generated/live";
import { useContext } from "preact/hooks";
import { Icon } from "../ui/icon";
import { RollChip } from "../ui/roll_chip";
import { Term } from "../ui/term";
import { sumLine } from "./breakdown";
import { Button } from "./controls";
import { EditContext } from "./edits";
import { interpolate, type Labels } from "./labels";
import { useOpenPopup } from "./popups";
import { plainRoll, rollLabel } from "./rolls";
import type { SheetView } from "./sheet_view";
import { allSpent, listedSpells, perDayLabel, slotCircles, spellMeta } from "./slots";
import { type Spent, Uses } from "./uses";

// A spell's name opens its text on the sheet; a sheet with no spell endpoint (a retainer) has none to open. `preview` reads it from
// the picker's endpoint, for a spell the sheet does not store (a ritual list's).
export function SpellName({
	spell,
	preview = false,
	class: className = "",
}: {
	spell: Pick<LiveSheetSpell, "spell_id" | "circle" | "name">;
	preview?: boolean;
	class?: string;
}) {
	const open = useOpenPopup();
	return (
		<button
			type="button"
			class={`cs-tap text-left font-bold text-link hover:text-link-hover ${className}`}
			onClick={(event) => open?.({ kind: "spell", spellId: spell.spell_id, circle: spell.circle, name: spell.name, preview }, event.currentTarget)}
		>
			{spell.name}
		</button>
	);
}

function SpellRow({ spell, spent, labels }: { spell: LiveSheetSpell; spent: Spent; labels: Labels }) {
	const meta = spellMeta(spell);
	return (
		<li class="flex flex-wrap items-center gap-x-2 gap-y-1 border-b border-line px-2.5 py-1.5 last:border-b-0">
			<SpellName spell={spell} class={allSpent(spell.uses, spent) ? "line-through opacity-60" : ""} />
			{spell.rolls.map((roll) => (
				<RollChip
					key={`${roll.kind}${roll.notation}`}
					size="sm"
					spec={plainRoll(roll.notation, interpolate(labels.descriptions.spell_roll, { label: rollLabel(roll, labels), name: spell.name }))}
				>
					{spell.rolls.length > 1 && <span class="mr-1 text-xs font-normal text-ink-muted">{rollLabel(roll, labels)}</span>}
					{roll.prefix && <span class="mr-1">{roll.prefix}</span>}
					{roll.notation}
				</RollChip>
			))}
			<span class="ml-auto">
				<Uses name={spell.name} uses={spell.uses} spent={spent} labels={labels} />
			</span>
			{meta && <span class="basis-full text-xs text-ink-muted">{meta}</span>}
		</li>
	);
}

const CARD_HEAD = "flex flex-wrap items-baseline justify-between gap-x-2 gap-y-0.5 px-2.5 py-1.5";

function CircleCard({
	circle,
	spells,
	view,
	spent,
	labels,
}: {
	circle: number | null;
	spells: LiveSheetSpell[];
	view: SheetView;
	spent: Spent;
	labels: Labels;
}) {
	const slot = circle == null ? undefined : view.derived.spell_slots.find((entry) => entry.circle === circle);
	return (
		<div class="cs-box overflow-hidden">
			{circle != null && (
				<div class={`${CARD_HEAD} border-b border-line bg-surface-sunken`}>
					<b class="cs-title text-[15px]">{interpolate(labels.spells.circle, { circle })}</b>
					{slot && (
						<Term
							class="text-[13px]"
							label={perDayLabel(slot, spells, labels)}
							title={`${labels.glossary.per_day.title} · ${interpolate(labels.spells.circle, { circle })}`}
							text={labels.glossary.per_day.text}
							lines={[sumLine(slot.breakdown, labels)]}
						/>
					)}
				</div>
			)}
			{spells.length > 0 ? (
				<ul>
					{spells.map((spell) => (
						<SpellRow key={spell.id} spell={spell} spent={spent} labels={labels} />
					))}
				</ul>
			) : (
				<p class="px-2.5 py-1.5 text-ink-muted">{labels.spells.none_memorized}</p>
			)}
		</div>
	);
}

// The circle the class opens at a later level; a high casting stat may already grant spells in it, which wait for that level.
function LockedCard({ locked, labels }: { locked: LiveLockedCircle; labels: Labels }) {
	const circle = interpolate(labels.spells.circle, { circle: locked.circle });
	const extra =
		locked.extra > 0
			? [
					interpolate(labels.spells.locked_extra, {
						stat: labels.attribute_names[locked.stat],
						extra: locked.extra,
						circle: locked.circle,
						level: locked.level,
					}),
				]
			: [];
	return (
		<div class={`${CARD_HEAD} rounded-xl border border-dashed border-line-strong`}>
			<Term
				label={interpolate(labels.spells.locked, { circle, level: locked.level })}
				title={labels.glossary.locked_circle.title ?? ""}
				text={labels.glossary.locked_circle.text}
				lines={extra}
			/>
		</div>
	);
}

// `editable`: the viewer may edit the sheet, so the checklist that writes the book is on offer.
function Grimoire({ spells, labels, editable }: { spells: LiveSheetSpell[]; labels: Labels; editable: boolean }) {
	const open = useOpenPopup();
	return (
		<div class="cs-box cs-box-grimoire mt-2 overflow-hidden">
			<div class="flex flex-wrap items-center gap-2 px-2.5 py-1.5">
				<Icon name="grimoire" class="h-4 w-4 text-dourado-600 dark:text-dourado-300" />
				<b class="cs-title text-[15px]">{labels.spells.grimoire}</b>
				{editable && open && (
					<span class="ml-auto">
						<Button tone="alt" size="sm" icon="pen" label={labels.spells_picker.grimoire_edit} onClick={(button) => open({ kind: "grimoire" }, button)} />
					</span>
				)}
			</div>
			{spells.length > 0 ? (
				<ul class="flex flex-wrap gap-x-1.5 gap-y-1 px-2.5 pb-2.5 text-[13px]">
					{spells.map((spell) => (
						<li key={spell.id} class="rounded-full border border-line bg-surface-raised px-2 py-0.5">
							{spell.circle !== null && (
								<b class="mr-1 text-ink-muted">
									{interpolate(labels.spells[`circle_short_${spell.circle}`] ?? labels.spells.circle_short, { circle: spell.circle })}
								</b>
							)}
							<SpellName spell={spell} />
							{spell.circle !== null && spell.times_memorized === 0 && <i class="text-ink-muted"> · {labels.spells.grimoire_only}</i>}
						</li>
					))}
				</ul>
			) : (
				<p class="px-2.5 pb-2.5 text-ink-muted">{labels.spells.grimoire_empty}</p>
			)}
		</div>
	);
}

export function Spells({ ability, view, spent, labels }: { ability: LiveSheetAbility; view: SheetView; spent: Spent; labels: Labels }) {
	const edits = useContext(EditContext);
	const open = useOpenPopup();
	const editable = edits !== null;
	const spells = view.spells.filter((spell) => spell.ability_id === ability.id);
	const listed = listedSpells(spells);
	const circles = slotCircles(
		ability.casts ? view.derived.spell_slots : [],
		listed.map((spell) => spell.circle),
	);
	const locked = ability.casts ? view.derived.locked_circles : [];
	const grimoire = view.spells.filter((spell) => spell.in_grimoire);
	if (circles.length === 0 && locked.length === 0 && !ability.spellbook) return null;
	return (
		<div class="mt-1">
			{editable && ability.casts && open && (
				<div class="mb-1.5 flex justify-end">
					<Button tone="alt" size="sm" icon="spellBook" label={labels.spells_picker.prepare} onClick={(button) => open({ kind: "spells" }, button)} />
				</div>
			)}
			<div class="cs-circles">
				{circles.map((circle) => (
					<CircleCard
						key={circle ?? "exclusive"}
						circle={circle}
						spells={listed.filter((spell) => spell.circle === circle)}
						view={view}
						spent={spent}
						labels={labels}
					/>
				))}
				{locked.map((entry) => (
					<LockedCard key={entry.circle} locked={entry} labels={labels} />
				))}
			</div>
			{ability.spellbook && <Grimoire spells={grimoire} labels={labels} editable={editable} />}
		</div>
	);
}
